import { GQL } from "@binale-tech/shared";

import { AUTOMATION_JOB_POLL_MS, isJobFinished } from "@domain/graphql/automationJob";

import { withThinking } from "../runner/runAgent";
import type { MessageId } from "@domain/intl/useTranslation";

import type { AgentContext, AgentDetailItem, AgentIO, AgentStep, ReportLine } from "../runner/types";
import { planDraftJobs, type DraftJob } from "./draftSelection";

const MAX_POLL_ERRORS = 3;
export const JOB_STALL_MS = 600_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type JobEnd =
    | { kind: "finished"; job: GQL.IAutomationJob }
    | { kind: "cancelled" | "stalled"; job: GQL.IAutomationJob | null };

/** Polls one job until it ends, the run is closed or its progress stalls. */
const pollJob = async (ctx: AgentContext, io: AgentIO, jobId: string, progressId: string, total: number) => {
    let errors = 0;
    let last: GQL.IAutomationJob | null = null;
    let lastDone = -1;
    let lastChange = Date.now();
    for (;;) {
        try {
            last = await ctx.readAutomationJob(jobId);
            errors = 0;
        } catch (error) {
            errors += 1;
            if (errors >= MAX_POLL_ERRORS) throw error;
        }
        if (last) {
            const done = last.succeeded + last.skipped + last.failed;
            io.update(progressId, { progress: { done: done, total } });
            if (done !== lastDone) {
                lastDone = done;
                lastChange = Date.now();
            }
            if (isJobFinished(last)) return { kind: "finished", job: last } satisfies JobEnd;
        }
        if (io.isCancelled()) return { kind: "cancelled", job: last } satisfies JobEnd;
        if (Date.now() - lastChange >= JOB_STALL_MS) return { kind: "stalled", job: last } satisfies JobEnd;
        await sleep(AUTOMATION_JOB_POLL_MS);
    }
};

const reasonLine = (reason: GQL.IDmsDocumentToRecordReason, count: number): ReportLine => ({
    textId: "app.agent.report.draftRefused",
    values: { count },
    labels: { reason: `app.automation.d2r.reason.${reason}` },
});

const Outcome = GQL.IDmsDocumentToRecordOutcome;
const Mode = GQL.IDmsDocumentToRecordMode;

const createdTexts: Record<GQL.IDmsDocumentToRecordMode, MessageId> = {
    [Mode.Template]: "app.agent.details.drafts.created.template",
    [Mode.Predicted]: "app.agent.details.drafts.created.predicted",
    [Mode.DocumentOnly]: "app.agent.details.drafts.noSuggestion",
    [Mode.FallbackNoAccount]: "app.agent.details.drafts.noAccount",
};

const refusedText = (reason: GQL.IDmsDocumentToRecordReason | null | undefined): MessageId =>
    reason ? `app.automation.d2r.reason.${reason}` : "app.agent.details.drafts.failed";

/** One item per created record (mode decides the text), else one item for the document. */
const toDetails = (r: GQL.IDmsDocumentToRecordResult, product: GQL.IProductKey): AgentDetailItem[] => {
    const ref = { kind: "document" as const, id: String(r.documentId) };
    if (r.outcome === Outcome.Created) {
        const textId = (r.mode ? createdTexts[r.mode] : undefined) ?? "app.agent.details.drafts.created";
        // no account or no suggestion for it: the draft needs the user
        const group = r.mode === Mode.FallbackNoAccount || r.mode === Mode.DocumentOnly ? "needsYou" : "done";
        if (r.recordIds.length === 0) return [{ group, ref, outcome: { textId } }];
        return r.recordIds.map((id) => ({
            group,
            ref,
            target: { kind: "record" as const, id: String(id), product },
            outcome: { textId },
        }));
    }
    if (r.outcome === Outcome.AlreadyBooked) {
        return [{ group: "untouched", ref, outcome: { textId: "app.agent.details.drafts.alreadyBooked" } }];
    }
    return [{ group: "refused", ref, outcome: { textId: refusedText(r.reason) } }];
};

/** The open line and the refused rows of Bank and Kasse documents without a group; kept when the drafts are declined. */
const withoutGroupResult = (count: number, ids: string[]): { open: ReportLine[]; details: AgentDetailItem[] } => ({
    open: count > 0 ? [reasonLine(GQL.IDmsDocumentToRecordReason.MissingGroup, count)] : [],
    details: ids.map((id) => ({
        group: "refused",
        ref: { kind: "document", id },
        outcome: { textId: refusedText(GQL.IDmsDocumentToRecordReason.MissingGroup) },
    })),
});

export const draftsFromDocumentsStep: AgentStep = {
    id: "draftsFromDocuments",
    async run(ctx, io) {
        if (!ctx.canRead(GQL.IProductKey.Dms)) {
            io.narrateSkipped(GQL.IProductKey.Dms);
            return { status: "skipped", skipReason: "noRight" };
        }
        const targets = [GQL.IProductKey.Er, GQL.IProductKey.Bank, GQL.IProductKey.Kb];
        if (!targets.some((p) => ctx.canWrite(p))) {
            for (const p of targets) io.narrateSkipped(p);
            return { status: "skipped", skipReason: "noRight" };
        }
        const found = await withThinking(io, "app.agent.drafts.thinking", () => ctx.findDraftDocuments());
        const { jobs, withoutGroup, withoutGroupIds } = planDraftJobs(found, ctx.documentGroupId);
        if (jobs.length === 0 && withoutGroup === 0) return { status: "nothing" };

        const writable: DraftJob[] = [];
        for (const job of jobs) {
            if (ctx.canWrite(job.productKey)) writable.push(job);
            else io.narrateSkipped(job.productKey);
        }
        if (writable.length > 0) {
            const approved = await io.approve({
                textId: "app.agent.drafts.approve.question",
                values: { count: writable.reduce((sum, job) => sum + job.documentIds.length, 0) },
                goLabelId: "app.agent.drafts.approve.go",
                learnMoreId: "app.agent.drafts.learnMore",
            });
            if (!approved)
                return { status: "skipped", byUser: true, ...withoutGroupResult(withoutGroup, withoutGroupIds) };
        }

        const refused = new Map<GQL.IDmsDocumentToRecordReason, number>();
        const addRefused = (reason: GQL.IDmsDocumentToRecordReason, n = 1) =>
            refused.set(reason, (refused.get(reason) ?? 0) + n);
        if (withoutGroup > 0) addRefused(GQL.IDmsDocumentToRecordReason.MissingGroup, withoutGroup);

        const open: ReportLine[] = [];
        const details: AgentDetailItem[] = [];
        let created = 0;
        let failed = 0;
        let running = 0;
        let jobFailed = false;
        let continuesOnServer = false;

        const runJob = async (job: DraftJob): Promise<boolean> => {
            const total = job.documentIds.length;
            const labels = { product: `app.titles.${job.productKey}` } as const;
            const progressId = io.post({
                kind: "progress",
                textId: "app.agent.drafts.progress",
                labels,
                progress: { done: 0, total },
            });
            const jobId = await ctx.enqueueDocumentsToRecords(job.productKey, job.groupId, job.documentIds);
            const end = await pollJob(ctx, io, jobId, progressId, total);
            if (end.kind !== "finished") {
                running += total;
                details.push(
                    ...job.documentIds.map((id): AgentDetailItem => ({
                        group: "untouched",
                        ref: { kind: "document", id },
                        outcome: { textId: "app.agent.details.drafts.running" },
                    })),
                );
                continuesOnServer = true;
                if (end.kind === "stalled") io.post({ kind: "text", textId: "app.agent.continuesOnServer" });
                return false;
            }
            const results = end.job.documentsToRecordsResults ?? [];
            let made = 0;
            const jobDetails = results.flatMap((r) => toDetails(r, job.productKey));
            details.push(...jobDetails);
            for (const r of results) {
                if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Created) {
                    made += 1;
                    ctx.created.recordIds.push(...r.recordIds);
                } else if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Refused && r.reason) {
                    addRefused(r.reason);
                } else if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Failed) {
                    if (r.reason) addRefused(r.reason);
                    else failed += 1;
                }
            }
            if (end.job.status === GQL.IAutomationJobStatus.Failed) jobFailed = true;
            if (results.length === 0 && end.job.status === GQL.IAutomationJobStatus.Failed) failed += total;
            created += made;
            if (made > 0) {
                io.post({
                    kind: "done",
                    textId: "app.agent.drafts.done",
                    values: { count: made },
                    labels,
                    details: jobDetails,
                });
            }
            return true;
        };

        for (const job of writable) {
            if (io.isCancelled()) break;
            if (!(await runJob(job))) break;
        }

        details.push(...withoutGroupResult(withoutGroup, withoutGroupIds).details);
        for (const [reason, count] of refused) open.push(reasonLine(reason, count));
        if (failed > 0) open.push({ textId: "app.agent.report.draftsFailed", values: { count: failed } });
        if (running > 0) open.push({ textId: "app.agent.report.draftsRunning", values: { count: running } });

        const summary: ReportLine[] =
            created > 0 ? [{ textId: "app.agent.report.draftsCreated", values: { count: created } }] : [];
        const status = jobFailed ? "failed" : created > 0 || continuesOnServer ? "done" : "nothing";
        return { status, summary, open, details, ...(continuesOnServer ? { data: { continuesOnServer: true } } : {}) };
    },
};
